// UltraFlex Admin Portal - Audit Log Viewer
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Search, Filter, Download, RefreshCw, ChevronLeft, ChevronRight,
  User, Shield, CreditCard, MapPin, Package, Settings, AlertCircle,
  Clock, Eye, Edit, Trash2, Plus, CheckCircle, XCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminAuditLogs = () => {
  const { token } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Filters
  const [filters, setFilters] = useState({
    action_type: '',
    user_id: '',
    entity_type: '',
    date_from: '',
    date_to: '',
    search: ''
  });
  const [showFilters, setShowFilters] = useState(false);

  // Selected log for detail view
  const [selectedLog, setSelectedLog] = useState(null);

  useEffect(() => {
    fetchLogs();
  }, [page]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '25'
      });
      
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.search) params.append('search', filters.search);

      const res = await fetch(`${API_URL}/api/audit-logs?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLogs(data.items || []);
      setTotalPages(data.total_pages || 1);
      setTotal(data.total || 0);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    setPage(1);
    fetchLogs();
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({
      action_type: '',
      user_id: '',
      entity_type: '',
      date_from: '',
      date_to: '',
      search: ''
    });
    setPage(1);
    fetchLogs();
  };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams({ format: 'csv' });
      if (filters.action_type) params.append('action_type', filters.action_type);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const res = await fetch(`${API_URL}/api/audit-logs/export?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `audit_logs_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      alert('Export failed');
    }
  };

  const getActionIcon = (actionType) => {
    const icons = {
      'create': <Plus className="w-4 h-4 text-green-400" />,
      'update': <Edit className="w-4 h-4 text-blue-400" />,
      'delete': <Trash2 className="w-4 h-4 text-red-400" />,
      'login': <User className="w-4 h-4 text-purple-400" />,
      'logout': <User className="w-4 h-4 text-gray-400" />,
      'access_granted': <CheckCircle className="w-4 h-4 text-green-400" />,
      'access_denied': <XCircle className="w-4 h-4 text-red-400" />,
      'payment': <CreditCard className="w-4 h-4 text-yellow-400" />,
      'freeze': <AlertCircle className="w-4 h-4 text-blue-400" />,
    };
    return icons[actionType] || <Settings className="w-4 h-4 text-gray-400" />;
  };

  const getEntityIcon = (entityType) => {
    const icons = {
      'member': <User className="w-4 h-4" />,
      'staff': <Shield className="w-4 h-4" />,
      'site': <MapPin className="w-4 h-4" />,
      'order': <Package className="w-4 h-4" />,
      'subscription': <CreditCard className="w-4 h-4" />,
      'asset': <Settings className="w-4 h-4" />,
    };
    return icons[entityType] || <Settings className="w-4 h-4" />;
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString('en-GB'),
      time: date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
    };
  };

  const actionTypes = [
    'create', 'update', 'delete', 'login', 'logout', 
    'access_granted', 'access_denied', 'payment', 'refund', 
    'freeze', 'unfreeze', 'suspend', 'reactivate'
  ];

  const entityTypes = [
    'member', 'staff', 'site', 'subscription', 'order', 
    'product', 'asset', 'mandate', 'collection', 'entitlement'
  ];

  return (
    <div className="p-6" data-testid="audit-logs-page">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-uf-text">Audit Logs</h1>
            <p className="text-uf-text-muted mt-1">Track all system activities and changes</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${showFilters ? 'bg-uf-red/20 border-uf-red text-uf-red' : 'border-uf-border text-uf-text hover:border-uf-red'}`}
            >
              <Filter className="w-4 h-4" />
              Filters
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-2 px-4 py-2 rounded-lg border border-uf-border text-uf-text hover:border-uf-red"
            >
              <Download className="w-4 h-4" />
              Export
            </button>
            <button
              onClick={fetchLogs}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-uf-red text-white hover:bg-uf-red-hover"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-uf-text-muted" />
          <input
            type="text"
            placeholder="Search by user, action, or entity ID..."
            value={filters.search}
            onChange={(e) => setFilters({...filters, search: e.target.value})}
            onKeyDown={(e) => e.key === 'Enter' && handleApplyFilters()}
            className="w-full pl-12 pr-4 py-3 bg-uf-surface border border-uf-border rounded-lg text-uf-text placeholder-uf-text-muted focus:border-uf-red focus:outline-none"
          />
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-uf-surface border border-uf-border rounded-lg p-6 mb-6" data-testid="filter-panel">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <div>
                <label className="block text-sm text-uf-text-muted mb-2">Action Type</label>
                <select
                  value={filters.action_type}
                  onChange={(e) => setFilters({...filters, action_type: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-bg border border-uf-border rounded-lg text-uf-text"
                >
                  <option value="">All Actions</option>
                  {actionTypes.map(type => (
                    <option key={type} value={type}>{type.replace('_', ' ')}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-2">Entity Type</label>
                <select
                  value={filters.entity_type}
                  onChange={(e) => setFilters({...filters, entity_type: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-bg border border-uf-border rounded-lg text-uf-text"
                >
                  <option value="">All Entities</option>
                  {entityTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-2">From Date</label>
                <input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({...filters, date_from: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-bg border border-uf-border rounded-lg text-uf-text"
                />
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-2">To Date</label>
                <input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({...filters, date_to: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-bg border border-uf-border rounded-lg text-uf-text"
                />
              </div>
              <div>
                <label className="block text-sm text-uf-text-muted mb-2">User ID</label>
                <input
                  type="text"
                  placeholder="Filter by user..."
                  value={filters.user_id}
                  onChange={(e) => setFilters({...filters, user_id: e.target.value})}
                  className="w-full px-4 py-2 bg-uf-bg border border-uf-border rounded-lg text-uf-text"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={handleApplyFilters}
                className="px-6 py-2 bg-uf-red text-white rounded-lg hover:bg-uf-red-hover"
              >
                Apply Filters
              </button>
              <button
                onClick={handleClearFilters}
                className="px-6 py-2 border border-uf-border text-uf-text rounded-lg hover:bg-uf-surface"
              >
                Clear All
              </button>
            </div>
          </div>
        )}

        {/* Stats Bar */}
        <div className="flex items-center gap-6 mb-4 text-sm text-uf-text-muted">
          <span>Total: <strong className="text-uf-text">{total}</strong> entries</span>
          <span>Page: <strong className="text-uf-text">{page}</strong> of {totalPages}</span>
        </div>

        {/* Logs Table */}
        <div className="bg-uf-surface border border-uf-border rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-uf-border bg-uf-bg">
                  <th className="text-left px-4 py-3 text-sm font-medium text-uf-text-muted">Timestamp</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-uf-text-muted">Action</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-uf-text-muted">Entity</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-uf-text-muted">User</th>
                  <th className="text-left px-4 py-3 text-sm font-medium text-uf-text-muted">Description</th>
                  <th className="text-center px-4 py-3 text-sm font-medium text-uf-text-muted">Details</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-uf-red mx-auto"></div>
                    </td>
                  </tr>
                ) : logs.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-12 text-uf-text-muted">
                      No audit logs found
                    </td>
                  </tr>
                ) : (
                  logs.map((log) => {
                    const ts = formatTimestamp(log.timestamp || log.created_at);
                    return (
                      <tr 
                        key={log.id} 
                        className="border-b border-uf-border hover:bg-uf-bg/50 transition-colors"
                        data-testid={`log-row-${log.id}`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-uf-text-muted">
                            <Clock className="w-4 h-4" />
                            <div>
                              <p className="text-uf-text text-sm">{ts.date}</p>
                              <p className="text-xs">{ts.time}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            {getActionIcon(log.action_type || log.action)}
                            <span className="text-uf-text capitalize">{(log.action_type || log.action || '').replace('_', ' ')}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2 text-uf-text-muted">
                            {getEntityIcon(log.entity_type)}
                            <div>
                              <p className="text-uf-text text-sm capitalize">{log.entity_type}</p>
                              <p className="text-xs font-mono truncate max-w-[120px]">{log.entity_id}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-uf-text text-sm">{log.user_name || log.performed_by || 'System'}</p>
                            <p className="text-uf-text-muted text-xs">{log.user_type || 'staff'}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-uf-text-muted text-sm truncate max-w-[250px]">
                            {log.description || log.details || '-'}
                          </p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <button
                            onClick={() => setSelectedLog(log)}
                            className="p-2 hover:bg-uf-bg rounded-lg text-uf-text-muted hover:text-uf-red transition-colors"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-uf-border">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page === 1}
                className="flex items-center gap-2 px-4 py-2 border border-uf-border rounded-lg text-uf-text disabled:opacity-50 disabled:cursor-not-allowed hover:border-uf-red"
              >
                <ChevronLeft className="w-4 h-4" />
                Previous
              </button>
              <div className="flex gap-2">
                {[...Array(Math.min(5, totalPages))].map((_, i) => {
                  const pageNum = Math.max(1, Math.min(page - 2 + i, totalPages - 4 + i));
                  return (
                    <button
                      key={i}
                      onClick={() => setPage(pageNum)}
                      className={`w-10 h-10 rounded-lg ${page === pageNum ? 'bg-uf-red text-white' : 'border border-uf-border text-uf-text hover:border-uf-red'}`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page === totalPages}
                className="flex items-center gap-2 px-4 py-2 border border-uf-border rounded-lg text-uf-text disabled:opacity-50 disabled:cursor-not-allowed hover:border-uf-red"
              >
                Next
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {selectedLog && (
          <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-uf-surface rounded-xl border border-uf-border max-w-2xl w-full max-h-[80vh] overflow-hidden" data-testid="log-detail-modal">
              <div className="flex items-center justify-between px-6 py-4 border-b border-uf-border">
                <h3 className="text-lg font-semibold text-uf-text">Audit Log Details</h3>
                <button onClick={() => setSelectedLog(null)} className="text-uf-text-muted hover:text-uf-text">
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="grid grid-cols-2 gap-6 mb-6">
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Log ID</p>
                    <p className="text-uf-text font-mono text-sm">{selectedLog.id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Timestamp</p>
                    <p className="text-uf-text">{new Date(selectedLog.timestamp || selectedLog.created_at).toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Action Type</p>
                    <div className="flex items-center gap-2">
                      {getActionIcon(selectedLog.action_type || selectedLog.action)}
                      <span className="text-uf-text capitalize">{(selectedLog.action_type || selectedLog.action || '').replace('_', ' ')}</span>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Entity Type</p>
                    <p className="text-uf-text capitalize">{selectedLog.entity_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Entity ID</p>
                    <p className="text-uf-text font-mono text-sm">{selectedLog.entity_id}</p>
                  </div>
                  <div>
                    <p className="text-sm text-uf-text-muted mb-1">Performed By</p>
                    <p className="text-uf-text">{selectedLog.user_name || selectedLog.performed_by || 'System'}</p>
                  </div>
                </div>
                
                {selectedLog.description && (
                  <div className="mb-6">
                    <p className="text-sm text-uf-text-muted mb-1">Description</p>
                    <p className="text-uf-text">{selectedLog.description}</p>
                  </div>
                )}

                {(selectedLog.old_values || selectedLog.new_values || selectedLog.changes) && (
                  <div>
                    <p className="text-sm text-uf-text-muted mb-2">Changes</p>
                    <div className="bg-uf-bg rounded-lg p-4 overflow-x-auto">
                      <pre className="text-sm text-uf-text font-mono whitespace-pre-wrap">
                        {JSON.stringify(selectedLog.changes || { old: selectedLog.old_values, new: selectedLog.new_values }, null, 2)}
                      </pre>
                    </div>
                  </div>
                )}

                {selectedLog.ip_address && (
                  <div className="mt-6 pt-6 border-t border-uf-border">
                    <p className="text-sm text-uf-text-muted">IP Address: <span className="text-uf-text">{selectedLog.ip_address}</span></p>
                    {selectedLog.user_agent && (
                      <p className="text-sm text-uf-text-muted mt-1">User Agent: <span className="text-uf-text">{selectedLog.user_agent}</span></p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminAuditLogs;
